import classnames from 'classnames';
import type { FC, ReactNode } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { TagButton } from '#src/atoms/buttons/TagButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconChevronDown } from '#src/atoms/icons/system/iconChevronDown.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

export type Selection = [string, string | undefined, string | undefined];
type SubSection = Record<string, undefined | string[]>;
export type TreeSchema = Record<string, undefined | SubSection>;

type Props = {
  schema: TreeSchema;
  selected: Selection;
  onSelect: (selected: Selection) => void;
  title: ReactNode;
  children: ReactNode;
  labels?: Record<string, string>;
  renderSub2?: (props: {
    options: string[];
    selected?: string;
    onSelect: (option: string) => void;
  }) => ReactNode;
};

const Root = styled(Vertical).attrs({ gap: 3 })`
  margin-top: var(--double-unit);
`;

const SideBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  border-bottom: 1px solid var(--c-nd-100);
  padding-bottom: var(--double-unit);
  button {
    text-align: left;
  }
  @media ${laptopAndAbove} {
    min-width: 220px;
    border-bottom: none;
  }
`;
const Section = styled(Vertical).attrs({ gap: 0 })`
  justify-content: flex-start;
`;
const Sub1 = styled(Vertical)`
  display: none;
  margin-left: var(--double-unit);
  justify-content: flex-start;
  &.selected {
    display: flex;
  }
`;
const Selected = styled(ButtonBase)`
  display: flex;
  justify-content: space-between;
  color: var(--c-white);
`;
const Container = styled.div`
  @media ${laptopAndAbove} {
    display: flex;
  }
`;
const Content = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Sub2 = styled.div`
  display: flex;
  white-space: nowrap;
  overflow: auto;
  gap: var(--unit);
`;

const Button: FC<
  React.PropsWithChildren<{ onClick: () => void; selected: boolean }>
> = ({ onClick, selected, children }) => (
  <BodyM
    color={selected ? 'var(--c-blue-400)' : 'var(--c-nd-600)'}
    as="button"
    onClick={onClick}
    type="button"
    bold={selected}
  >
    {children}
  </BodyM>
);

export const Tree = ({
  schema,
  selected,
  onSelect,
  title,
  labels,
  children,
  renderSub2,
}: Props) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const [open, toggleOpen] = useToggle(false);
  const [selectedSection, selectedSub1, selectedSub2] = selected;

  const label = (key: string) => labels?.[key] || key;

  const selectSub1 = (section: string, sub1?: string) => {
    if (!schema[section]) return undefined;

    return sub1 || selectedSub1 || Object.keys(schema[section]!)[0];
  };

  const selectSub2 = (section: string, sub1?: string, sub2?: string) => {
    const s1 = selectSub1(section, sub1);
    if (!s1) return undefined;
    if (!schema[section]![s1]) return undefined;

    return sub2 || selectedSub2 || schema[section]![s1]?.[0];
  };

  const select = (section: string, sub1?: string, sub2?: string) => () => {
    const s1 = selectSub1(section, sub1);
    const s2 = selectSub2(section, sub1, sub2);
    onSelect([section, s1, s2]);
    if (!sub2) toggleOpen();
  };

  const sub2Options = selectedSub1
    ? schema[selectedSection]?.[selectedSub1]
    : undefined;

  const renderSideBar = () => (
    <SideBar>
      {Object.entries(schema).map(([section, subSections]) => (
        <Section key={section}>
          <Button
            selected={selectedSection === section}
            onClick={select(section)}
          >
            {label(section)}
          </Button>
          {!!subSections && (
            <Sub1
              className={classnames({
                selected: selectedSection === section,
              })}
            >
              {Object.keys(subSections).map(subSection => (
                <Button
                  key={`${section}/${subSection}`}
                  selected={selectedSub1 === subSection}
                  onClick={select(section, subSection)}
                >
                  {label(subSection)}
                </Button>
              ))}
            </Sub1>
          )}
        </Section>
      ))}
    </SideBar>
  );

  const renderDropdown = () => (
    <Vertical>
      <Selected onClick={toggleOpen}>
        <BodyM bold>
          {label(selectedSection)}
          {!!selectedSub1 && <>&nbsp;/&nbsp;{label(selectedSub1)}</>}
        </BodyM>
        <FontAwesomeIcon icon={iconChevronDown} />
      </Selected>
      {!!open && renderSideBar()}
    </Vertical>
  );

  return (
    <Root>
      {!isLaptop && renderDropdown()}
      <div>{title}</div>
      <Container>
        {!!isLaptop && renderSideBar()}
        <Content>
          {!!sub2Options &&
            (renderSub2 ? (
              renderSub2({
                options: sub2Options,
                selected: selectedSub2,
                onSelect: option =>
                  select(selectedSection, selectedSub1, option)(),
              })
            ) : (
              <Sub2>
                {sub2Options.map(sub2Title => (
                  <TagButton
                    key={sub2Title}
                    onClick={select(selectedSection, selectedSub1, sub2Title)}
                    selected={sub2Title === selectedSub2}
                  >
                    {label(sub2Title)}
                  </TagButton>
                ))}
              </Sub2>
            ))}
          <div>{children}</div>
        </Content>
      </Container>
    </Root>
  );
};
